import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 60px 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  color: #333;
  line-height: 1.7;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const AppIcon = styled.img`
  width: 88px;
  height: 88px;
  margin: 0 auto 1rem;
  border-radius: 20px;
  display: block;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  color: #1a1a1a;
`;

const Tagline = styled.p`
  font-size: 1.1rem;
  color: #666;
`;

const StoreButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const AppStoreButton = styled.a`
  display: inline-block;

  img {
    height: 50px;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.05);
    }
  }
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
`;

const Paragraph = styled.p`
  margin-bottom: 1rem;
  color: #444;
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin: 1.5rem 0;
`;

const StatusCard = styled.div<{ $color: string; $bgColor: string }>`
  text-align: center;
  padding: 1.25rem 1rem;
  border-radius: 12px;
  background: ${props => props.$bgColor};
  border: 2px solid ${props => props.$color};
`;

const StatusDot = styled.div<{ $color: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.$color};
  margin: 0 auto 0.5rem;
`;

const StatusLabel = styled.div`
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
`;

const StatusHint = styled.div`
  font-size: 0.85rem;
  color: #666;
`;

const StepNumber = styled.div`
  width: 28px;
  height: 28px;
  background: #4CAF50;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  margin-right: 1rem;
  flex-shrink: 0;
`;

const StepCard = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
`;

const StepContent = styled.div`
  flex: 1;
  font-size: 1rem;
  color: #333;
`;

const HighlightBox = styled.div`
  padding: 1.25rem;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-radius: 12px;
  margin: 1rem 0;
  text-align: center;
`;

const HighlightText = styled.p`
  font-size: 1.1rem;
  color: #2e7d32;
  margin: 0;
  font-weight: 500;
`;

const BenefitList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-top: 1rem;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  color: #444;

  span {
    margin-right: 0.5rem;
  }
`;

const MacCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const MacIcon = styled.img`
  width: 96px;
  height: 96px;
  border-radius: 22px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
`;

const MacInfo = styled.div`
  flex: 1;
`;

const StyledLink = styled(Link)`
  color: #4CAF50;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const ContactLink = styled.a`
  color: #4CAF50;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const Footer = styled.footer`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
  text-align: center;
  color: #888;
  font-size: 0.9rem;
`;

const TimeToGoInfo: React.FC = () => {
  return (
    <Container>
      <Header>
        <AppIcon src="/timetogo/appicon.png" alt="TimeToGo app icon" />
        <Title>TimeToGo</Title>
        <Tagline>Traffic at a glance—right in your Mac menu bar.</Tagline>
        <StoreButtons>
          <AppStoreButton
            href="https://apps.apple.com/app/id6758226358"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
              alt="Download on the App Store"
            />
          </AppStoreButton>
          <AppStoreButton
            href="https://apps.apple.com/app/id6758940999?pt=126701792&ct=ai4u-portfolio&mt=8"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="https://tools.applemediaservices.com/api/badges/download-on-the-mac-app-store/black/en-us?size=250x83"
              alt="Download on the Mac App Store"
            />
          </AppStoreButton>
        </StoreButtons>
      </Header>

      <Section>
        <SectionTitle>How It Works</SectionTitle>
        <Paragraph>
          Set up once, monitor effortlessly. TimeToGo watches the traffic for you.
        </Paragraph>

        <StepCard>
          <StepNumber>1</StepNumber>
          <StepContent>Set your home and work addresses</StepContent>
        </StepCard>

        <StepCard>
          <StepNumber>2</StepNumber>
          <StepContent>Place your phone on the desk — screen stays on</StepContent>
        </StepCard>

        <StepCard>
          <StepNumber>3</StepNumber>
          <StepContent>Glance at the color. Green? Tap to start navigation!</StepContent>
        </StepCard>

        <HighlightBox>
          <HighlightText>
            📳 Vibration alert when traffic clears up
          </HighlightText>
          <HighlightText style={{ marginTop: '0.5rem' }}>
            🗺️ One tap to launch Maps navigation
          </HighlightText>
        </HighlightBox>
      </Section>

      <Section>
        <SectionTitle>Traffic Status at a Glance</SectionTitle>
        <StatusGrid>
          <StatusCard $color="#4CAF50" $bgColor="#e8f5e9">
            <StatusDot $color="#4CAF50" />
            <StatusLabel>Green</StatusLabel>
            <StatusHint>Let's go!</StatusHint>
          </StatusCard>
          <StatusCard $color="#FF9800" $bgColor="#fff3e0">
            <StatusDot $color="#FF9800" />
            <StatusLabel>Yellow</StatusLabel>
            <StatusHint>Not bad</StatusHint>
          </StatusCard>
          <StatusCard $color="#f44336" $bgColor="#ffebee">
            <StatusDot $color="#f44336" />
            <StatusLabel>Red</StatusLabel>
            <StatusHint>Wait a bit</StatusHint>
          </StatusCard>
        </StatusGrid>
      </Section>

      <Section>
        <SectionTitle>Why TimeToGo?</SectionTitle>
        <BenefitList>
          <BenefitItem><span>📱</span> Screen stays on — no more unlocking</BenefitItem>
          <BenefitItem><span>📳</span> Haptic alert when traffic improves</BenefitItem>
          <BenefitItem><span>📍</span> No GPS tracking required</BenefitItem>
          <BenefitItem><span>🔒</span> No account needed</BenefitItem>
          <BenefitItem><span>🔄</span> Auto-refresh every 5 minutes</BenefitItem>
          <BenefitItem><span>🗺️</span> Tap to open in Maps app</BenefitItem>
        </BenefitList>
      </Section>

      <Section>
        <SectionTitle>Now on Mac</SectionTitle>
        <MacCard>
          <MacIcon src="/timetogo/appicon-mac.png" alt="TimeToGo for Mac icon" />
          <MacInfo>
            <Paragraph style={{ marginBottom: '0.75rem' }}>
              TimeToGo now runs natively on your Mac. Keep an eye on your commute
              right from the desktop — glance at the traffic color while you work,
              and head out the moment it turns green.
            </Paragraph>
            <BenefitList>
              <BenefitItem><span>🖥️</span> Native menu bar–style desktop app</BenefitItem>
              <BenefitItem><span>👀</span> Always-visible traffic status</BenefitItem>
              <BenefitItem><span>🔄</span> Auto-refresh in the background</BenefitItem>
              <BenefitItem><span>🔒</span> No account or TimeToGo backend</BenefitItem>
            </BenefitList>
            <AppStoreButton
              href="https://apps.apple.com/us/app/timetogo-monitor-traffic/id6758940999?mt=12"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginTop: '1.25rem' }}
            >
              <img
                src="https://tools.applemediaservices.com/api/badges/download-on-the-mac-app-store/black/en-us?size=250x83"
                alt="Download on the Mac App Store"
              />
            </AppStoreButton>
          </MacInfo>
        </MacCard>
      </Section>

      <Section>
        <SectionTitle>Privacy</SectionTitle>
        <Paragraph>
          <strong>Your saved route stays on your Mac.</strong> TimeToGo stores your
          route and preferred drive time locally. The Mac app has no account system or
          advertising. It uses Firebase Analytics to measure feature usage and upgrades;
          our custom events do not include route addresses or precise coordinates.
        </Paragraph>
        <Paragraph>
          Traffic requests are handled by Apple MapKit using random identifiers that are
          not linked to your identity. These requests go directly to Apple. Google/Firebase
          receives usage events and SDK-collected data, including an app-instance identifier.
          See the policy below for platform differences and privacy choices.
        </Paragraph>
        <Paragraph>
          <StyledLink to="/timetogo/privacy">Read our Privacy Policy →</StyledLink>
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Contact & Support</SectionTitle>
        <Paragraph>
          Questions or feedback? Reach out:
        </Paragraph>
        <Paragraph>
          <ContactLink href="mailto:support@ai4u.co.nz">support@ai4u.co.nz</ContactLink>
        </Paragraph>
      </Section>

      <Footer>
        <p>© 2026 AI4U. All rights reserved.</p>
        <p>TimeToGo v1.5 · Now on iPhone & Mac</p>
      </Footer>
    </Container>
  );
};

export default TimeToGoInfo;
